import { useState, useCallback, useRef } from 'react';

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

interface RateLimiterResult {
  isRateLimited: boolean;
  remainingRequests: number;
  checkRateLimit: () => boolean;
  resetRateLimit: () => void;
}

/**
 * Client-side rate limiter hook for form submissions
 * Prevents rapid submissions and bot abuse
 */
export function useRateLimiter(config: RateLimitConfig = { maxRequests: 5, windowMs: 60000 }): RateLimiterResult {
  const [requestCount, setRequestCount] = useState(0);
  const windowStartRef = useRef<number>(Date.now());

  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();
    
    // Reset window if expired
    if (now - windowStartRef.current > config.windowMs) {
      windowStartRef.current = now;
      setRequestCount(1);
      return false; // Not rate limited
    }
    
    // Check if rate limited
    if (requestCount >= config.maxRequests) {
      return true; // Rate limited
    }
    
    // Increment counter
    setRequestCount(prev => prev + 1);
    return false; // Not rate limited
  }, [requestCount, config.maxRequests, config.windowMs]);

  const resetRateLimit = useCallback(() => {
    setRequestCount(0);
    windowStartRef.current = Date.now();
  }, []);

  return {
    isRateLimited: requestCount >= config.maxRequests,
    remainingRequests: Math.max(0, config.maxRequests - requestCount),
    checkRateLimit,
    resetRateLimit,
  };
}
